import axios from 'axios';
import { notify } from "@/main";
import secureStorage from '@/utils/secureStorage';
import { API } from '@/config/api';

const state = {
    uploadProgress: 0,
    isUploading: false,
    lastUploadResponse: null,
};

const mutations = {
    SET_UPLOAD_PROGRESS(state, progress) {
        state.uploadProgress = progress;
    },
    SET_UPLOADING(state, isUploading) {
        state.isUploading = isUploading;
    },
    SET_LAST_UPLOAD_RESPONSE(state, response) {
        state.lastUploadResponse = response;
    },
    RESET_UPLOAD_STATE(state) {
        state.uploadProgress = 0;
        state.isUploading = false;
        state.lastUploadResponse = null;
    },
};

const actions = {
    /**
     * Upload and transcribe video/audio file
     * @param {Object} payload - { file, language, userId, companyId, groupIds, typeOfTranscription, onProgress }
     */
    async uploadAndTranscribe({ commit }, {
        file,
        language,
        userId,
        companyId,
        groupIds = [],
        typeOfTranscription = 1,
        onProgress
    }) {
        commit('SET_UPLOADING', true);
        commit('SET_UPLOAD_PROGRESS', 0);

        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("language", language);
            formData.append("typeOfTranscription", typeOfTranscription);

            const userIdAsArray = JSON.stringify(Array.isArray(userId) ? userId : [userId]);
            formData.append("userIds", userIdAsArray);
            formData.append("companieId", companyId);

            // Add group IDs if provided
            if (groupIds.length > 0) {
                const groupIdsAsArray = JSON.stringify(groupIds);
                formData.append("groupIds", groupIdsAsArray);
            }

            const config = {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                    commit('SET_UPLOAD_PROGRESS', progress);
                    if (onProgress) {
                        onProgress(progress);
                    }
                },
            };

            const response = await axios.post(
                API.TRANSCRIBE,
                formData,
                config
            );

            commit('SET_LAST_UPLOAD_RESPONSE', response.data);
            secureStorage.setItem("response", response.data);

            notify("File uploaded successfully!", "success");

            return { success: true, data: response.data };
        } catch (error) {
            
            notify("Upload failed", "error");
            return { success: false, error };
        } finally {
            commit('SET_UPLOADING', false);
        }
    },

    /**
     * Upload and transcribe from YouTube URL
     * @param {Object} payload - { youtubeUrl, language, userId, companyId, groupIds, typeOfTranscription }
     */
    async uploadYouTubeVideo({ commit }, {
        youtubeUrl,
        language,
        userId,
        companyId,
        groupIds = [],
        typeOfTranscription = 5
    }) {
        commit('SET_UPLOADING', true);

        try {
            const userIdAsArray = JSON.stringify(Array.isArray(userId) ? userId : [userId]);
            const groupIdsAsArray = groupIds.length > 0 ? JSON.stringify(groupIds) : null;

            const formData = new FormData();
            formData.append("youtube_url", youtubeUrl);
            formData.append("language", language);
            formData.append("typeOfTranscription", typeOfTranscription);
            formData.append("userIds", userIdAsArray);
            formData.append("companieId", companyId);

            if (groupIdsAsArray) {
                formData.append("groupIds", groupIdsAsArray);
            }

            const config = {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            };

            const response = await axios.post(
                API.TRANSCRIBE,
                formData,
                config
            );

            commit('SET_LAST_UPLOAD_RESPONSE', response.data);
            secureStorage.setItem("response", response.data);

            notify("YouTube video submitted for transcription!", "success");

            return { success: true, data: response.data };
        } catch (error) {
            
            notify("YouTube upload failed", "error");
            return { success: false, error };
        } finally {
            commit('SET_UPLOADING', false);
        }
    },

    /**
     * Upload audio recording for transcription
     * @param {Object} payload - { audioBlob, language, userId, companyId, duration }
     */
    async uploadAudioRecording({ commit }, {
        audioBlob,
        language,
        userId,
        companyId,
        duration
    }) {
        commit('SET_UPLOADING', true);

        try {
            const formData = new FormData();
            const audioFile = new File([audioBlob], "recording.webm", {
                type: "audio/webm",
            });

            formData.append("file", audioFile);
            formData.append("language", language);
            formData.append("typeOfTranscription", 4); // Audio recording type
            formData.append("duration", duration);

            const userIdAsArray = JSON.stringify([userId]);
            formData.append("userIds", userIdAsArray);
            formData.append("companieId", companyId);

            const response = await axios.post(
                API.TRANSCRIBE,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            commit('SET_LAST_UPLOAD_RESPONSE', response.data);

            notify("Your audio has been successfully submitted for transcription!", "success");

            return { success: true, data: response.data };
        } catch (error) {
            
            notify("Audio upload failed", "error");
            return { success: false, error };
        } finally {
            commit('SET_UPLOADING', false);
        }
    },

    /**
     * Upload subtitle file for transcription
     * @param {Object} payload - { file, language, userId, companyId, documentName, originalLanguage }
     */
    async uploadSubtitleFile({ commit }, {
        file,
        language,
        userId,
        companyId,
        documentName,
        originalLanguage
    }) {
        commit('SET_UPLOADING', true);
        commit('SET_UPLOAD_PROGRESS', 0);

        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("language", language);
            formData.append("typeOfTranscription", 6); // Subtitle type
            formData.append("document_name", documentName);
            formData.append("original_language", originalLanguage);

            const userIdAsArray = JSON.stringify([userId]);
            formData.append("userIds", userIdAsArray);
            formData.append("companieId", companyId);

            const config = {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                    commit('SET_UPLOAD_PROGRESS', progress);
                },
            };

            const response = await axios.post(
                API.TRANSCRIBE,
                formData,
                config
            );

            commit('SET_LAST_UPLOAD_RESPONSE', response.data);

            notify("Subtitle file uploaded successfully!", "success");

            return { success: true, data: response.data };
        } catch (error) {
            
            notify("Subtitle upload failed", "error");
            return { success: false, error };
        } finally {
            commit('SET_UPLOADING', false);
        }
    },

    resetUploadState({ commit }) {
        commit('RESET_UPLOAD_STATE');
    },
};

const getters = {
    uploadProgress: state => state.uploadProgress,
    isUploading: state => state.isUploading,
    lastUploadResponse: state => state.lastUploadResponse,
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
